import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Briefcase, CheckCircle, CreditCard, FileText, User, X } from 'lucide-react';

export function IDComparison({ check, verificationData, setVerificationData, handleFieldVerification }) {
    const candidateData = {
      ...(check?.subcheck_data || {}),
      father_name: check?.father_name || check?.subcheck_data?.father_name || '',
      dob: check?.dob || check?.subcheck_data?.dob || check?.subcheck_data?.date_of_birth || '',
      candidate_name: check?.subcheck_data?.candidate_name || check?.subcheck_data?.name || check?.employee_name || '',
    };
    const checkType = check?.check_type?.toLowerCase() || '';
    
    // Verification status options with color codes
    const verificationStatusOptions = [
      { value: 'verified', label: 'Verified', color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-50 border-green-300' },
      { value: 'discrepancy', label: 'Discrepancy', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50 border-red-300' },
      { value: 'partially_verified', label: 'Partially Verified', color: 'bg-sky-400', textColor: 'text-sky-700', bgLight: 'bg-sky-50 border-sky-300' },
      { value: 'not_verified', label: 'Not Verified', color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50 border-amber-300' },
    ];
    
    // Get status color classes
    const getStatusClasses = (status) => {
      const found = verificationStatusOptions.find(o => o.value === status);
      return found || { color: 'bg-gray-300', textColor: 'text-gray-600', bgLight: 'bg-white border-gray-200' };
    };

    // Determine the ID type based on check type and sub_option
    const getIDType = () => {
      const fullType = `${checkType} ${(check?.subcheck_type || '').toLowerCase()} ${(check?.sub_option || '').toLowerCase()}`;
      if (fullType.includes('pan')) return { label: 'PAN Card', placeholder: 'ABCDE1234F' };
      if (fullType.includes('aadhar') || fullType.includes('aadhaar')) return { label: 'Aadhar Card', placeholder: '1234 5678 9012' };
      if (fullType.includes('passport')) return { label: 'Passport', placeholder: 'A1234567' };
      if (fullType.includes('voter') || fullType.includes('epic')) return { label: 'Voter ID', placeholder: 'ABC1234567' };
      if (fullType.includes('driving') || fullType.includes('dl')) return { label: 'Driving License', placeholder: 'DL-1234567890' };
      if (fullType.includes('uan')) return { label: 'UAN (PF)', placeholder: '123456789012' };
      if (fullType.includes('credit')) return { label: 'Credit Check', placeholder: 'Credit Score/Report' };
      if (fullType.includes('database')) return { label: 'Database Check', placeholder: 'Database ID' };
      return { label: 'ID Document', placeholder: 'ID Number' };
    };

    const idType = getIDType();

    const idVerificationMethods = [
      { value: 'online_portal', label: 'Government Portal Verification' },
      { value: 'database_check', label: 'Database Check' },
      { value: 'physical_document', label: 'Physical Document Verification' },
      { value: 'third_party', label: 'Third Party Service' },
      { value: 'api_verification', label: 'API Verification' },
      { value: 'manual_check', label: 'Manual Cross-Check' },
    ];

    const verificationSources = [
      { value: 'nsdl', label: 'NSDL (PAN)' },
      { value: 'uidai', label: 'UIDAI (Aadhar)' },
      { value: 'epfo', label: 'EPFO (UAN/PF)' },
      { value: 'cibil', label: 'CIBIL' },
      { value: 'transunion_experian', label: 'TransUnion / Experian' },
      { value: 'experian', label: 'Experian' },
      { value: 'equifax', label: 'Equifax' },
      { value: 'crif', label: 'CRIF High Mark' },
      { value: 'refinitiv', label: 'Refinitiv World-Check' },
      { value: 'passport_seva', label: 'Passport Seva' },
      { value: 'parivahan', label: 'Parivahan (DL)' },
      { value: 'eci', label: 'Election Commission' },
      { value: 'other', label: 'Other Source' },
    ];
    
    // Clear form function for ID verification
    const clearIDForm = () => {
      setVerificationData(prev => ({
        ...prev,
        verified_details: {
          ...prev.verified_details,
          candidate_name: '',
          name_status: '',
          father_name: '',
          father_status: '',
          dob: '',
          dob_status: '',
          id_number: '',
          id_status: '',
          address: '',
          address_status: '',
          verification_source: '',
          verification_method: '',
          document_authenticity: '',
          issue_date: '',
          expiry_date: '',
          verification_date: '',
          credit_score: '',
          score_range: '',
          active_loans: '',
          total_outstanding: '',
          defaults_status: '',
          recent_enquiries: '',
          uan_status: '',
          pf_balance: '',
          member_ids: '',
          last_employer_uan: '',
          uan_employment_history: '',
        }
      }));
      toast.info('Form cleared');
    };
    
    return (
      <div className="space-y-4">
        {/* Header with Clear Button */}
        <div className="flex items-center justify-between bg-gradient-to-r from-slate-800 to-slate-700 text-white px-4 py-3 rounded-lg shadow-lg">
          <h4 className="font-semibold text-base flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            {idType.label} Verification - {check?.subcheck_type || idType.label}
          </h4>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={clearIDForm}
            className="bg-white/10 hover:bg-white/20 text-white border-white/30 hover:border-white/50"
          >
            <X className="w-4 h-4 mr-1" />
            Clear Form
          </Button>
        </div>
        
        {/* Status Color Legend */}
        <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg border">
          <span className="text-xs font-medium text-slate-600">Status Legend:</span>
          <div className="flex items-center gap-3">
            {verificationStatusOptions.map(opt => (
              <div key={opt.value} className="flex items-center gap-1">
                <div className={`w-3 h-3 rounded-full ${opt.color}`}></div>
                <span className={`text-xs ${opt.textColor}`}>{opt.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Main Comparison Grid */}
        <div className="grid grid-cols-2 gap-4">
          {/* Candidate Provided (Left) */}
          <Card className="border-2 border-blue-200 shadow-md bg-gradient-to-b from-blue-50 to-white">
            <CardHeader className="pb-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-t-lg">
              <CardTitle className="text-sm flex items-center gap-2">
                <User className="w-4 h-4" />
                Candidate Provided Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm pt-4">
              <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                <span className="text-blue-600 block text-xs font-medium">Candidate Name</span>
                <span className="font-semibold text-slate-800">{candidateData.candidate_name || candidateData.name || check?.employee_name || '-'}</span>
              </div>
              
              <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                <span className="text-blue-600 block text-xs font-medium">Father's Name</span>
                <span className="font-semibold text-slate-800">{candidateData.father_name || candidateData.fathers_name || '-'}</span>
              </div>
              
              <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                <span className="text-blue-600 block text-xs font-medium">Date of Birth</span>
                <span className="font-semibold text-slate-800">{candidateData.dob || candidateData.date_of_birth || '-'}</span>
              </div>
              
              <div className="p-3 bg-white rounded-lg border-2 border-blue-300 shadow-sm">
                <span className="text-blue-600 block text-xs font-medium">{idType.label} Number</span>
                <span className="font-bold text-lg text-slate-800">{candidateData.id_number || candidateData.pan_number || candidateData.aadhar_number || candidateData.uan_number || candidateData.document_number || '-'}</span>
              </div>
              
              <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                <span className="text-blue-600 block text-xs font-medium">Present Address</span>
                <p className="font-medium">{candidateData.address || candidateData.present_address || candidateData.complete_address || '-'}</p>
              </div>
              
              {candidateData.gender && (
                <div className="p-2 bg-blue-50 rounded">
                  <span className="text-xs text-blue-600 font-medium">Gender</span>
                  <p className="font-medium">{candidateData.gender}</p>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Verified Details (Right) */}
          <Card className="border-cyan-200 bg-gradient-to-b from-cyan-50 to-white">
            <CardHeader className="pb-2 bg-cyan-100/50">
              <CardTitle className="text-sm text-cyan-800 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Verified Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-3">
              {/* Name */}
              <div className="p-2 bg-white rounded-lg border">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs font-medium">Candidate Name</Label>
                  <Select 
                    value={verificationData.verified_details.name_status || ''} 
                    onValueChange={(v) => handleFieldVerification('name_status', true, v)}
                  >
                    <SelectTrigger className="h-6 w-28 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      {verificationStatusOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Input 
                  value={verificationData.verified_details.candidate_name || ''} 
                  onChange={(e) => handleFieldVerification('candidate_name', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="Name as per ID document"
                />
              </div>
              
              {/* Father's Name */}
              <div className="p-2 bg-white rounded-lg border">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs font-medium">Father's Name</Label>
                  <Select 
                    value={verificationData.verified_details.father_status || ''} 
                    onValueChange={(v) => handleFieldVerification('father_status', true, v)}
                  >
                    <SelectTrigger className="h-6 w-28 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      {verificationStatusOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Input 
                  value={verificationData.verified_details.father_name || ''} 
                  onChange={(e) => handleFieldVerification('father_name', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="Father's name as per document"
                />
              </div>
              
              {/* DOB */}
              <div className="p-2 bg-white rounded-lg border">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs font-medium">Date of Birth</Label>
                  <Select 
                    value={verificationData.verified_details.dob_status || ''} 
                    onValueChange={(v) => handleFieldVerification('dob_status', true, v)}
                  >
                    <SelectTrigger className="h-6 w-28 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      {verificationStatusOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Input 
                  type="date"
                  value={verificationData.verified_details.dob || ''} 
                  onChange={(e) => handleFieldVerification('dob', true, e.target.value)}
                  className="h-7 text-sm"
                />
              </div>
              
              {/* ID Number */}
              <div className="p-2 bg-white rounded-lg border-2 border-cyan-300">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs font-medium">{idType.label} Number</Label>
                  <Select 
                    value={verificationData.verified_details.id_status || ''} 
                    onValueChange={(v) => handleFieldVerification('id_status', true, v)}
                  >
                    <SelectTrigger className="h-6 w-28 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      {verificationStatusOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Input 
                  value={verificationData.verified_details.id_number || ''} 
                  onChange={(e) => handleFieldVerification('id_number', true, e.target.value)}
                  className="h-8 text-sm font-mono font-bold"
                  placeholder={idType.placeholder}
                />
              </div>
              
              {/* Address */}
              <div className="p-2 bg-white rounded-lg border">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs font-medium">Address (as per ID)</Label>
                  <Select 
                    value={verificationData.verified_details.address_status || ''} 
                    onValueChange={(v) => handleFieldVerification('address_status', true, v)}
                  >
                    <SelectTrigger className="h-6 w-28 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      {verificationStatusOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Textarea 
                  value={verificationData.verified_details.address || ''} 
                  onChange={(e) => handleFieldVerification('address', true, e.target.value)}
                  className="h-14 text-sm resize-none"
                  placeholder="Address as per ID document"
                />
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* ID Document Verification Details */}
        <Card className="border-indigo-200">
          <CardHeader className="pb-2 bg-gradient-to-r from-indigo-50 to-purple-50">
            <CardTitle className="text-sm text-indigo-800 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Document Verification Details
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 pt-3">
            <div className="space-y-3">
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Verification Source</Label>
                <Select 
                  value={verificationData.verified_details.verification_source || ''} 
                  onValueChange={(v) => handleFieldVerification('verification_source', true, v)}
                >
                  <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Select source" /></SelectTrigger>
                  <SelectContent>
                    {verificationSources.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Verification Method</Label>
                <Select 
                  value={verificationData.verified_details.id_verification_method || ''} 
                  onValueChange={(v) => handleFieldVerification('id_verification_method', true, v)}
                >
                  <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Select method" /></SelectTrigger>
                  <SelectContent>
                    {idVerificationMethods.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Document Authenticity</Label>
                <Select 
                  value={verificationData.verified_details.document_authenticity || ''} 
                  onValueChange={(v) => handleFieldVerification('document_authenticity', true, v)}
                >
                  <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="genuine">Genuine / Authentic</SelectItem>
                    <SelectItem value="verified_online">Verified Online - Authentic</SelectItem>
                    <SelectItem value="suspicious">Suspicious / Needs Review</SelectItem>
                    <SelectItem value="fake">Fake / Forged</SelectItem>
                    <SelectItem value="expired">Expired Document</SelectItem>
                    <SelectItem value="not_provided">Not Provided</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-3">
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Issue Date</Label>
                <Input 
                  type="date"
                  value={verificationData.verified_details.issue_date || ''} 
                  onChange={(e) => handleFieldVerification('issue_date', true, e.target.value)}
                  className="h-7 text-sm"
                />
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Expiry Date (if applicable)</Label>
                <Input 
                  type="date"
                  value={verificationData.verified_details.expiry_date || ''} 
                  onChange={(e) => handleFieldVerification('expiry_date', true, e.target.value)}
                  className="h-7 text-sm"
                />
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Verification Date</Label>
                <Input 
                  type="date"
                  value={verificationData.verified_details.id_verification_date || new Date().toISOString().split('T')[0]} 
                  onChange={(e) => handleFieldVerification('id_verification_date', true, e.target.value)}
                  className="h-7 text-sm"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Credit Check Specific Fields */}
        {checkType.includes('credit') && (
          <Card className="border-emerald-200">
            <CardHeader className="pb-2 bg-gradient-to-r from-emerald-50 to-teal-50">
              <CardTitle className="text-sm text-emerald-800 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Credit Report Details
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-3 gap-4 pt-3">
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Credit Score</Label>
                <Input 
                  value={verificationData.verified_details.credit_score || ''} 
                  onChange={(e) => handleFieldVerification('credit_score', true, e.target.value)}
                  className="h-7 text-sm font-bold"
                  placeholder="e.g., 750"
                />
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Score Range</Label>
                <Select 
                  value={verificationData.verified_details.credit_score_range || ''} 
                  onValueChange={(v) => handleFieldVerification('credit_score_range', true, v)}
                >
                  <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excellent">Excellent (750+)</SelectItem>
                    <SelectItem value="good">Good (700-749)</SelectItem>
                    <SelectItem value="fair">Fair (650-699)</SelectItem>
                    <SelectItem value="poor">Poor (550-649)</SelectItem>
                    <SelectItem value="very_poor">Very Poor (&lt;550)</SelectItem>
                    <SelectItem value="no_history">No Credit History</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Active Loans</Label>
                <Input 
                  value={verificationData.verified_details.active_loans || ''} 
                  onChange={(e) => handleFieldVerification('active_loans', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="Count"
                />
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Total Outstanding</Label>
                <Input 
                  value={verificationData.verified_details.total_outstanding || ''} 
                  onChange={(e) => handleFieldVerification('total_outstanding', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="Amount in ₹"
                />
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Defaults/Write-offs</Label>
                <Select 
                  value={verificationData.verified_details.defaults_status || ''} 
                  onValueChange={(v) => handleFieldVerification('defaults_status', true, v)}
                >
                  <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Defaults</SelectItem>
                    <SelectItem value="minor">Minor Default (30-60 days)</SelectItem>
                    <SelectItem value="major">Major Default (60-90 days)</SelectItem>
                    <SelectItem value="write_off">Write-off/Settlement</SelectItem>
                    <SelectItem value="multiple">Multiple Defaults</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Credit Enquiries (6 months)</Label>
                <Input 
                  value={verificationData.verified_details.recent_enquiries || ''} 
                  onChange={(e) => handleFieldVerification('recent_enquiries', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="Count"
                />
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* UAN/PF Specific Fields */}
        {checkType.includes('uan') && (
          <Card className="border-orange-200">
            <CardHeader className="pb-2 bg-gradient-to-r from-orange-50 to-amber-50">
              <CardTitle className="text-sm text-orange-800 flex items-center gap-2">
                <Briefcase className="w-4 h-4" />
                UAN / PF Verification Details
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 pt-3">
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">UAN Status</Label>
                <Select 
                  value={verificationData.verified_details.uan_status || ''} 
                  onValueChange={(v) => handleFieldVerification('uan_status', true, v)}
                >
                  <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="withdrawn">PF Withdrawn</SelectItem>
                    <SelectItem value="transferred">Transferred</SelectItem>
                    <SelectItem value="not_found">Not Found</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">PF Balance (if available)</Label>
                <Input 
                  value={verificationData.verified_details.pf_balance || ''} 
                  onChange={(e) => handleFieldVerification('pf_balance', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="Amount in ₹"
                />
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Member IDs Count</Label>
                <Input 
                  value={verificationData.verified_details.member_ids_count || ''} 
                  onChange={(e) => handleFieldVerification('member_ids_count', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="Number of linked employers"
                />
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Last Employer (as per UAN)</Label>
                <Input 
                  value={verificationData.verified_details.last_employer_uan || ''} 
                  onChange={(e) => handleFieldVerification('last_employer_uan', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="Company name"
                />
              </div>
              <div className="col-span-2 p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Employment History (from UAN)</Label>
                <Textarea 
                  value={verificationData.verified_details.uan_employment_history || ''} 
                  onChange={(e) => handleFieldVerification('uan_employment_history', true, e.target.value)}
                  className="h-16 text-sm resize-none"
                  placeholder="List of employers linked to UAN..."
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Verification Remarks */}
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <Label className="text-xs font-medium mb-2 block">ID Verification Remarks</Label>
            <Textarea 
              value={verificationData.verified_details.id_remarks || ''} 
              onChange={(e) => handleFieldVerification('id_remarks', true, e.target.value)}
              className="h-16 text-sm resize-none"
              placeholder="Any additional observations or remarks about the ID verification..."
            />
          </CardContent>
        </Card>
      </div>
    );
}
